/* eslint-disable max-classes-per-file */
import { type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { IMAGE_UPLOAD_MAX_FILE_SIZE } from '../config';

export default class MediaUploadError extends Error {
  userFacingDescriptor?: MessageDescriptor;

  retryable?: true;

  /** The failure can never succeed on a later attempt, so auto-retries are skipped. */
  permanent?: true;

  /** The class name for metric tags, since minification rewrites `constructor.name`. */
  errorType = 'MediaUploadError';

  constructor(public message: string) {
    super(message);
    this.name = 'MediaUploadError';
    // Set the prototype explicitly, see https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // extending classes will also need to do this
    Object.setPrototypeOf(this, MediaUploadError.prototype);
  }
}

export class MediaUploadExpoError extends MediaUploadError {
  errorType = 'MediaUploadExpoError';

  static message = 'Expo Module Error';

  static userFacingMessage = msg`React Native Error, please contact customer service`;

  constructor(message: string) {
    super(`Expo Module Error: ${message}`);
    Object.setPrototypeOf(this, MediaUploadExpoError.prototype);
    this.userFacingDescriptor = MediaUploadExpoError.userFacingMessage;
  }
}

export class MediaUploadNotAuthorizedError extends MediaUploadError {
  errorType = 'MediaUploadNotAuthorizedError';

  static message = 'Not Authorized';

  static userFacingMessage = msg`Not Authorized`;

  constructor() {
    super(MediaUploadNotAuthorizedError.message);
    Object.setPrototypeOf(this, MediaUploadNotAuthorizedError.prototype);
    this.userFacingDescriptor = MediaUploadNotAuthorizedError.userFacingMessage;
  }
}

export class MediaUploadNetworkError extends MediaUploadError {
  errorType = 'MediaUploadNetworkError';

  static message = 'Network Error';

  static userFacingMessage = msg`Network Error, try again`;

  constructor() {
    super(MediaUploadNetworkError.message);
    this.retryable = true;
    Object.setPrototypeOf(this, MediaUploadNetworkError.prototype);
    this.userFacingDescriptor = MediaUploadNetworkError.userFacingMessage;
  }
}

export class MediaUploadSourceMissingError extends MediaUploadError {
  errorType = 'MediaUploadSourceMissingError';

  static message = 'Source File Missing';

  static userFacingMessage = msg`This file is no longer available on your device, please select it again`;

  constructor() {
    super(MediaUploadSourceMissingError.message);
    this.permanent = true;
    Object.setPrototypeOf(this, MediaUploadSourceMissingError.prototype);
    this.userFacingDescriptor = MediaUploadSourceMissingError.userFacingMessage;
  }
}

export class MediaUploadStorageFullError extends MediaUploadError {
  errorType = 'MediaUploadStorageFullError';

  static message = 'Device Storage Full';

  static userFacingMessage = msg`Not enough storage on your device, free up space and try again`;

  constructor() {
    super(MediaUploadStorageFullError.message);
    this.permanent = true;
    Object.setPrototypeOf(this, MediaUploadStorageFullError.prototype);
    this.userFacingDescriptor = MediaUploadStorageFullError.userFacingMessage;
  }
}

export class MediaUploadMaxFileSizeError extends MediaUploadError {
  errorType = 'MediaUploadMaxFileSizeError';

  static message = `Maximum image upload file size of ${IMAGE_UPLOAD_MAX_FILE_SIZE} exceeded`;

  static userFacingMessage = msg`File size too large, please use a smaller file`;

  constructor() {
    super(MediaUploadMaxFileSizeError.message);
    Object.setPrototypeOf(this, MediaUploadMaxFileSizeError.prototype);
    this.userFacingDescriptor = MediaUploadMaxFileSizeError.userFacingMessage;
  }
}

export class MediaUploadClientError extends MediaUploadError {
  errorType = 'MediaUploadClientError';

  static message = 'Client Error';

  static userFacingMessage = msg`Client Error, please wait and try again`;

  constructor() {
    super(MediaUploadClientError.message);
    Object.setPrototypeOf(this, MediaUploadClientError.prototype);
    this.userFacingDescriptor = MediaUploadClientError.userFacingMessage;
  }
}

export class MediaUploadServerError extends MediaUploadError {
  errorType = 'MediaUploadServerError';

  static message = 'Server Error';

  static userFacingMessage = msg`Server Error, please wait and try again`;

  constructor() {
    super(MediaUploadServerError.message);
    Object.setPrototypeOf(this, MediaUploadServerError.prototype);
    this.userFacingDescriptor = MediaUploadServerError.userFacingMessage;
  }
}

export class MediaUploadUnknownError extends MediaUploadError {
  errorType = 'MediaUploadUnknownError';

  static userFacingMessage = msg`Unknown error, please contact customer service if it persists`;

  constructor(public sourceErr: unknown) {
    super(`Unknown error: ${sourceErr}`);
    Object.setPrototypeOf(this, MediaUploadUnknownError.prototype);
    this.userFacingDescriptor = MediaUploadUnknownError.userFacingMessage;
  }
}
